import { useState } from "react";
import { toast } from "sonner";
import { ArrowRight, Loader2, Building2, Camera, Users, BadgeCheck } from "lucide-react";
import { API, IMG, CONTACT, INPUT_CLS } from "../lib/site";
import { Reveal, Eyebrow, SectionHead, EmberField, GoldButton } from "../components/motion";

const BENEFITS = [
  { icon: Users, title: "Guests Gather", copy: "Fire draws people in. Your terrace becomes the place the evening happens — and stays longer." },
  { icon: Camera, title: "Photographs Multiply", copy: "Guests photograph around the flame, with your brand burned into every frame. Organic, constant exposure." },
  { icon: BadgeCheck, title: "Identity Integrated", copy: "Your crest, your name, your emblem — part of the guest experience, not just a sign on the wall." },
  { icon: Building2, title: "Signature Collections", copy: "For selected partners: custom-designed series created exclusively for your property or clientele." },
];

const TIERS = [
  { range: "1 – 4 Tables", label: "Estate & Boutique", copy: "Private estates, boutique inns, executive retreats." },
  { range: "5 – 9 Tables", label: "Terrace Collection", copy: "Wineries, restaurants and golf clubs — a coordinated terrace identity." },
  { range: "10+ Tables", label: "Property Signature", copy: "Resorts and developers — a bespoke signature series, designed with you." },
];

export default function Hospitality() {
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const res = await fetch(`${API}/inquiries`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          inquiry_type: "hospitality-trade",
          message: form.company ? `Organization: ${form.company}\n\n${form.message}` : form.message,
        }),
      });
      if (!res.ok) throw new Error("send failed");
      setSent(true);
      toast.success("Your trade inquiry has been sent. Our partnerships desk will reply shortly.");
    } catch {
      toast.error(`The send was interrupted — please email ${CONTACT.email} directly.`);
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <section className="relative flex min-h-[78vh] items-end overflow-hidden">
        <img src={IMG.vineyard} alt="Luxury vineyard estate at dusk" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#080706] via-[#080706]/70 to-[#080706]/30" />
        <EmberField count={16} />
        <div className="relative mx-auto w-full max-w-7xl px-5 pb-20 pt-48 sm:px-10">
          <Reveal><Eyebrow>Hospitality & Trade</Eyebrow></Reveal>
          <Reveal delay={0.1}>
            <h1 className="mt-5 max-w-3xl font-serif text-4xl font-semibold leading-[1.08] tracking-tight text-stone-50 sm:text-5xl lg:text-6xl">
              Turn your brand into <span className="italic text-gold-gradient">part of the evening.</span>
            </h1>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-stone-300 sm:text-lg">
              For wineries, distilleries, restaurants, resorts, golf clubs and developers — a
              collection of Cozy Ember fire tables bearing your identity throughout the terrace.
            </p>
          </Reveal>
        </div>
      </section>

      <section data-testid="b2b-benefits" className="py-24 sm:py-32">
        <div className="mx-auto max-w-7xl px-5 sm:px-10">
          <SectionHead
            eyebrow="The Opportunity"
            title="An outdoor appliance becomes a conversation piece."
            copy="The fire draws them in. Then they notice the barrel, the stone, the detailing — and finally, the emblem that makes it unmistakably yours."
          />
          <div className="mt-16 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {BENEFITS.map((b, i) => (
              <Reveal key={b.title} delay={i * 0.1}>
                <div data-testid={`benefit-${b.title.toLowerCase().replace(/\s+/g, "-")}`} className="h-full rounded-2xl border border-stone-800 bg-[#141110] p-7 transition-[border-color,transform] duration-300 hover:-translate-y-1 hover:border-amber-500/40">
                  <b.icon size={22} className="text-amber-500" />
                  <h3 className="mt-5 font-serif text-lg text-stone-100">{b.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-stone-400">{b.copy}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="branding-showcase" className="border-y border-amber-900/20 bg-[#0c0a08] py-24 sm:py-32">
        <div className="mx-auto grid max-w-7xl items-center gap-14 px-5 sm:px-10 lg:grid-cols-2">
          <div>
            <SectionHead
              eyebrow="Your Emblem, Burned In"
              title="From showroom floor to signature."
              copy="A family name. An estate crest. A hospitality brand. We laser-etch and hand-finish your identity directly into the barrel staves — so the piece could never belong to anyone else."
            />
            <div className="mt-10 space-y-4">
              {TIERS.map((t, i) => (
                <Reveal key={t.label} delay={i * 0.08}>
                  <div data-testid={`tier-${t.label.toLowerCase().replace(/\s+/g, "-")}`} className="flex items-start gap-5 rounded-xl border border-stone-800 bg-[#141110] p-5">
                    <span className="font-serif text-xl text-gold-gradient whitespace-nowrap">{t.range}</span>
                    <div>
                      <p className="font-serif text-base text-stone-100">{t.label}</p>
                      <p className="mt-1 text-sm text-stone-400">{t.copy}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
          <Reveal delay={0.15} className="relative">
            <div className="animate-ember-pulse absolute -inset-4 rounded-[2rem] bg-amber-700/12 blur-3xl" />
            <div className="relative overflow-hidden rounded-[1.5rem] border border-amber-500/25 shadow-ember-lg">
              <img src={IMG.branding} alt="Whiskey barrel with custom branded engraving" className="aspect-[4/5] w-full object-cover" />
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent p-6">
                <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-amber-400">Concept Mockup</p>
                <p className="mt-1 font-serif text-lg italic text-stone-100">Your logo, hand-finished into the oak</p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section data-testid="trade-inquiry" className="py-24 sm:py-32">
        <div className="mx-auto grid max-w-7xl gap-14 px-5 sm:px-10 lg:grid-cols-[0.9fr_1.1fr]">
          <div>
            <SectionHead
              eyebrow="Partnerships Desk"
              title="Let's design your signature series."
              copy={`Tell us about your property and guest experience. Or write to the desk directly at ${CONTACT.email} — we speak terrace, vintage and volume.`}
            />
          </div>
          <Reveal delay={0.15}>
            {sent ? (
              <div data-testid="trade-success" className="rounded-2xl border border-amber-500/40 bg-amber-950/20 p-10 text-center">
                <BadgeCheck size={30} className="mx-auto text-amber-400" />
                <p className="mt-4 font-serif text-2xl text-stone-100">Received.</p>
                <p className="mt-2 text-sm text-stone-400">Our partnerships desk will reply to {form.email} within one business day.</p>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="trade-inquiry-form" className="space-y-4 rounded-2xl border border-stone-800 bg-[#141110] p-7 sm:p-9">
                <div className="grid gap-4 sm:grid-cols-2">
                  <input data-testid="trade-form-name" required minLength={2} placeholder="Your name" className={INPUT_CLS} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                  <input data-testid="trade-form-email" required type="email" placeholder="Work email" className={INPUT_CLS} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                </div>
                <input data-testid="trade-form-company" placeholder="Property / organization (winery, resort, club…)" className={INPUT_CLS} value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} />
                <textarea data-testid="trade-form-message" required minLength={5} rows={4} placeholder="Tell us about your terrace, guest experience, and how many tables you're imagining…" className={`${INPUT_CLS} resize-none`} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
                <GoldButton data-testid="trade-form-submit" type="submit" disabled={sending} className="w-full sm:w-auto">
                  {sending ? <Loader2 size={15} className="animate-spin" /> : <>Start the Conversation <ArrowRight size={15} /></>}
                </GoldButton>
              </form>
            )}
          </Reveal>
        </div>
      </section>
    </>
  );
}
